import { BigNumber } from '@ethersproject/bignumber';
import { LTV } from 'app/features/util/ltv';
import { sumNullableNumbers } from 'app/functions/silo/calculations';
import { SiloAsset } from 'app/queries/decoratedSilo';
import { useMemo } from 'react';
import { validator } from '../validator';
import { getLtvLimit } from './getLtvLimit';
import { MaxLendingAmount, MaxProtectedAmount } from './useMaxValues';

type UseValidation = (params: {
  selectedAsset: SiloAsset;
  normalizedLendingAmount: BigNumber | null;
  normalizedProtectedAmount: BigNumber | null;
  maxLendingAmount: MaxLendingAmount;
  maxProtectedAmount: MaxProtectedAmount;
  currentLTV: LTV | null;
  isFullLendingAmount: boolean;
  isFullProtectedAmount: boolean;
}) => {
  lendingError?: string;
  protectedError?: string;
  formError?: string;
  isInvalid: boolean;
};

export type ValidationProps = ReturnType<UseValidation>;

export const useValidation: UseValidation = ({
  selectedAsset,
  normalizedLendingAmount,
  normalizedProtectedAmount,
  maxLendingAmount,
  maxProtectedAmount,
  currentLTV,
  isFullLendingAmount,
  isFullProtectedAmount,
}) => {
  const lendingError = useMemo(() => {
    const result = validator({
      normalizedAmount: normalizedLendingAmount,
      maxAmount: maxLendingAmount.value.bigNumber,
      selectedAsset,
      balance: selectedAsset.collateralBalance,
      isProtected: false,
      isFullAmount: isFullLendingAmount,
    });

    return result?.message;
  }, [normalizedLendingAmount, maxLendingAmount, selectedAsset, isFullLendingAmount]);

  const protectedError = useMemo(() => {
    const result = validator({
      normalizedAmount: normalizedProtectedAmount,
      maxAmount: maxProtectedAmount.value.bigNumber,
      selectedAsset,
      balance: selectedAsset.collateralOnlyBalance,
      isProtected: true,
      isFullAmount: isFullProtectedAmount,
    });

    return result?.message;
  }, [normalizedProtectedAmount, maxProtectedAmount, selectedAsset, isFullProtectedAmount]);

  const formError = useMemo(
    () => getFormError({ currentLTV, selectedAsset, normalizedLendingAmount, normalizedProtectedAmount }),
    [currentLTV, selectedAsset, normalizedLendingAmount, normalizedProtectedAmount]
  );

  const isInvalid = !!(lendingError || protectedError || formError);

  return {
    lendingError,
    protectedError,
    formError,
    isInvalid,
  };
};
type GetFormError = (params: {
  currentLTV: LTV | null;
  selectedAsset: SiloAsset;
  normalizedLendingAmount: BigNumber | null;
  normalizedProtectedAmount: BigNumber | null;
}) => string | undefined;

const getFormError: GetFormError = ({
  currentLTV,
  selectedAsset,
  normalizedLendingAmount,
  normalizedProtectedAmount,
}) => {
  const ltvLimit = getLtvLimit(currentLTV, selectedAsset.assetAddress);

  if (!ltvLimit || !normalizedLendingAmount || !normalizedProtectedAmount) {
    return undefined;
  }

  const total = sumNullableNumbers([normalizedLendingAmount, normalizedProtectedAmount]);

  if (total.gte(ltvLimit.bigNumber)) {
    return 'You cannot withdraw an amount that would cause your position to exceed maximum LTV.';
  }

  return undefined;
};
